
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  CardDescription
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import PDFTemplateMapper from '@/components/student-certification/certificate-templates/PDFTemplateMapper';
import { CertificateTemplateElement } from '@/types/certificateTemplate';

const CreateTemplateMappingPage: React.FC = () => {
  const navigate = useNavigate();
  const [pdfData, setPdfData] = useState<{ url: string } | null>(null);
  const [mappedFields, setMappedFields] = useState<CertificateTemplateElement[]>([]);

  useEffect(() => {
    // Check for basic info
    const storedBasicInfo = sessionStorage.getItem('certificateTemplateBasicInfo');
    if (!storedBasicInfo) {
      toast.error('Please complete the basic information first');
      navigate('/certificate-templates/new/basic-info');
      return;
    }
    
    // Check for PDF data
    const storedPdfData = sessionStorage.getItem('certificateTemplatePdfData');
    if (storedPdfData) {
      const parsedPdfData = JSON.parse(storedPdfData);
      setPdfData(parsedPdfData);
    } else {
      // If we don't have PDF data, we can't map fields
      toast.info('No PDF template to map fields to');
    }
    
    // Load any previously saved mapping
    const storedMapping = sessionStorage.getItem('certificateTemplateMapping');
    if (storedMapping) {
      try {
        setMappedFields(JSON.parse(storedMapping));
      } catch (error) {
        console.error('Error parsing stored mapping:', error);
      }
    }
  }, [navigate]);

  const handleSaveMapping = (fields: CertificateTemplateElement[]) => {
    setMappedFields(fields);
    sessionStorage.setItem('certificateTemplateMapping', JSON.stringify(fields));
    toast.success('Field mapping saved successfully');
  };

  const handlePrevious = () => {
    navigate('/certificate-templates/new/upload');
  };

  const handleNext = () => {
    // We allow users to proceed without mapping fields
    navigate('/certificate-templates/new/preview');
  };

  const handleCancel = () => {
    navigate('/certificate-templates');
  };

  if (!pdfData?.url) {
    return (
      <div className="container mx-auto py-8">
        <Helmet>
          <title>Create Certificate Template - Field Mapping | NALC</title>
        </Helmet>
        
        <div className="mb-6">
          <h1 className="text-3xl font-bold">Create Certificate Template</h1>
          <p className="text-muted-foreground mt-2">
            Step 3: Map data fields to your PDF template
          </p>
        </div>
        
        <div className="flex justify-center mb-6">
          {/* Progress indicators */}
          <div className="flex items-center">
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 rounded-full bg-muted text-muted-foreground flex items-center justify-center">
                ✓
              </div>
              <span className="text-sm mt-1">Basic Info</span>
            </div>
            <div className="w-12 h-1 bg-primary mx-1"></div>
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 rounded-full bg-muted text-muted-foreground flex items-center justify-center">
                ✓
              </div>
              <span className="text-sm mt-1">PDF Upload</span>
            </div>
            <div className="w-12 h-1 bg-primary mx-1"></div>
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                3
              </div>
              <span className="text-sm mt-1 text-primary font-medium">Field Mapping</span>
            </div>
            <div className="w-12 h-1 bg-muted mx-1"></div>
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 rounded-full bg-muted text-muted-foreground flex items-center justify-center">
                4
              </div>
              <span className="text-sm mt-1">Preview</span>
            </div>
          </div>
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle>Field Mapping</CardTitle>
            <CardDescription>
              No PDF template was uploaded, so field mapping is not available. You can continue to the preview step or go back to upload a PDF.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="py-12 text-center">
              <p className="text-muted-foreground">No PDF template available for field mapping</p>
              <div className="mt-8 flex justify-center gap-4">
                <Button variant="outline" onClick={handlePrevious}>
                  <ChevronLeft className="mr-2 h-4 w-4" /> Go Back to Upload
                </Button>
                <Button onClick={handleNext}>
                  Skip to Preview <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      <Helmet>
        <title>Create Certificate Template - Field Mapping | NALC</title>
      </Helmet>
      
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Create Certificate Template</h1>
        <p className="text-muted-foreground mt-2">
          Step 3: Map data fields to your PDF template
        </p>
      </div>
      
      <div className="flex justify-center mb-6">
        {/* Progress indicators */}
        <div className="flex items-center">
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-muted text-muted-foreground flex items-center justify-center">
              ✓
            </div>
            <span className="text-sm mt-1">Basic Info</span>
          </div>
          <div className="w-12 h-1 bg-primary mx-1"></div>
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-muted text-muted-foreground flex items-center justify-center">
              ✓
            </div>
            <span className="text-sm mt-1">PDF Upload</span>
          </div>
          <div className="w-12 h-1 bg-primary mx-1"></div>
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
              3
            </div>
            <span className="text-sm mt-1 text-primary font-medium">Field Mapping</span>
          </div>
          <div className="w-12 h-1 bg-muted mx-1"></div>
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-muted text-muted-foreground flex items-center justify-center">
              4
            </div>
            <span className="text-sm mt-1">Preview</span>
          </div>
        </div>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>Field Mapping</CardTitle>
          <CardDescription>
            Define areas on the PDF template where dynamic content should be placed.
            Click and drag on the PDF to create fields, then assign data fields to them.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <PDFTemplateMapper 
            pdfUrl={pdfData.url}
            existingFields={mappedFields}
            onSaveMapping={handleSaveMapping}
          />
          
          <div className="flex justify-between mt-8 pt-4 border-t">
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="button" onClick={handlePrevious}>
                <ChevronLeft className="mr-2 h-4 w-4" /> Previous
              </Button>
            </div>
            <Button type="button" onClick={handleNext}>
              Continue <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateTemplateMappingPage;
